import { Button } from "@promptwatch/ui/components/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@promptwatch/ui/components/navigation-menu";
import { cn } from "@promptwatch/ui/lib/utils";
import { ArrowRight, ArrowUpRight, BookOpen, Building2, type LucideIcon, Mail, NotepadText, PencilRuler, Users } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useState } from "react";
import { clientEnv } from "@/env/client";

interface DropdownMenuItem {
  href: string;
  icon: LucideIcon;
  title: string;
  description: string;
}

interface DropdownMenuData {
  title: string;
  items: DropdownMenuItem[];
}

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

const ListItem = React.forwardRef<React.ElementRef<"a">, React.ComponentPropsWithoutRef<"a">>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              className,
            )}
            {...props}
          >
            <div className="text-sm font-medium leading-none">{title}</div>
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
          </a>
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = "ListItem";

const DropdownMenu: React.FC<DropdownMenuData> = ({ title, items }) => {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger>{title}</NavigationMenuTrigger>
      <NavigationMenuContent>
        <ul className="grid w-[380px] gap-1 p-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.href}>
                <NavigationMenuLink asChild>
                  <Link
                    href={item.href}
                    className="flex flex-row items-center gap-3 select-none rounded-md p-0 leading-none no-underline outline-none transition-colors hover:bg-gray-50/80 hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-gray-100">
                      <Icon className="h-10 w-10 text-gray-600" />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-medium leading-none">{item.title}</div>
                      <p className="line-clamp-2 text-xs leading-snug text-muted-foreground line-clamp-1">{item.description}</p>
                    </div>
                  </Link>
                </NavigationMenuLink>
              </li>
            );
          })}
        </ul>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const resourcesMenu: DropdownMenuData = {
  title: "Resources",
  items: [
    {
      href: "/blog",
      icon: NotepadText,
      title: "Blog",
      description: "Latest insights on GEO and AI search optimization",
    },
    {
      href: "/free-tools",
      icon: PencilRuler,
      title: "Free Tools",
      description: "Free tools to optimize your business for AI search",
    },
    {
      href: "/glossary",
      icon: BookOpen,
      title: "Glossary",
      description: "Free glossary of AI search terms",
    },
  ],
};

const solutionsMenu: DropdownMenuData = {
  title: "Solutions",
  items: [
    {
      href: "/for-agencies",
      icon: Users,
      title: "Agencies",
      description: "Manage multiple clients with our agency solutions",
    },
    {
      href: "/for-brands",
      icon: Building2,
      title: "Brands",
      description: "Enterprise solutions for brands and businesses",
    },
    {
      href: "/contact-us",
      icon: Mail,
      title: "Contact us",
      description: "Get in touch with our team for custom solutions",
    },
  ],
};

export default function Nav() {
  const posthog = usePostHog();
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loggedIn = getCookie("loggedIn") === "true";
    setIsLoggedIn(loggedIn);
  }, []);

  return (
    <header
      className={`inset-x-0 top-0 z-50 border-b border-gray-950/5 transition-all duration-300 sticky backdrop-blur-md ${scrolled ? "bg-gray-50/50" : "bg-transparent"}`}
    >
      <div className="relative mx-auto lg:border-x border-gray-950/5 max-w-6xl px-4 py-4 transition-all duration-300 h-auto lg:px-12">
        <nav className="flex flex-wrap items-center justify-between">
          <Link href="/" className="flex items-center gap-1" aria-label="Home">
            <Image className="h-9" src="/logo.svg" alt="Promptwatch logo" height={110} width={180} priority loading="eager" />
          </Link>

          <div className="hidden lg:flex">
            <span className="absolute inset-y-0 hidden rounded-full bg-gray-950/5 opacity-0 duration-300 ease-out group-hover:opacity-100 lg:block" />
            <NavigationMenu>
              <NavigationMenuList>
                {/* <NavigationMenuItem>
                  <Link href="/#features" legacyBehavior passHref>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>Answer Gap</NavigationMenuLink>
                  </Link>
                </NavigationMenuItem> */}

                <DropdownMenu {...solutionsMenu} />
                <DropdownMenu {...resourcesMenu} />

                <NavigationMenuItem>
                  <Link href="/book-a-demo" legacyBehavior passHref>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>Book a demo</NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link href="/pricing" legacyBehavior passHref>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>Pricing</NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex gap-2">
            {isLoggedIn ? (
              <Button
                variant="default"
                className="text-xs flex items-center gap-2"
                onClick={() => {
                  posthog.capture("open_dashboard_clicked", {
                    source: "nav",
                  });
                  router.push(`${clientEnv.NEXT_PUBLIC_APP_URL}`);
                }}
              >
                Open dashboard <ArrowUpRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="text-xs flex items-center gap-2"
                  onClick={() => {
                    posthog.capture("signin_button_clicked", {
                      source: "nav",
                    });
                    router.push(`${clientEnv.NEXT_PUBLIC_APP_URL}/signin?type=signin`);
                  }}
                >
                  Sign in
                </Button>

                <Button
                  variant="default"
                  className="text-xs flex items-center gap-2"
                  onClick={() => {
                    posthog.capture("get_started_button_clicked", {
                      source: "nav",
                    });
                    router.push(`${clientEnv.NEXT_PUBLIC_APP_URL}/signin?type=signup`);
                  }}
                >
                  Start 7-day trial <ArrowRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
